/**
 * Offene Punkte — was noch zu tun ist, in einer Liste.
 *
 * ═══ WARUM EINE LISTE UND NICHT VIER KÄSTEN ═══
 *
 * Vorher gab es „Belege" und „Nachpflege" als getrennte Bildschirme, jeder mit
 * eigener Darstellung: Belege als große Kacheln je Auftrag, Nachpflege als
 * Tabelle, Zuordnungskonflikte als Kasten obendrauf. Drei Formen für eine
 * Frage — und die Frage lautet immer gleich: Was muss ich anfassen, bevor
 * abgerechnet werden kann?
 *
 * Jetzt ist es eine Tabelle mit gleichen Zeilenhöhen und Filtern je Spalte,
 * wie die Disposition auch. Der wichtigste Gewinn ist nicht die Optik: Weil
 * alles in einer Liste steht, lässt sie sich nach ALTER sortieren. „Was liegt
 * am längsten" war über zwei Bildschirme hinweg nicht zu beantworten.
 *
 * ═══ WAS HIER NICHT MEHR IST ═══
 *
 * Das Ansehen der Belegbilder. Das gehört an die Tour — dort liegen sie, dort
 * kann man sie hochladen, verwerfen und die Beleglage freigeben. Eine zweite
 * Bildansicht wäre ein zweiter Ort für dieselbe Sache gewesen, und genau davon
 * kam der Bildschirm ja weg.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Seitenkopf } from '../Rahmen';
import {
  Fehlerkasten,
  Karte,
  Knopf,
  Ladehinweis,
  Marker,
  Meldung,
  Textfeld,
} from '../gestaltung/bausteine';
import { Filtertabelle, type Spalte } from '../gestaltung/Filtertabelle';
import { useAbfrage } from '../daten/abfrage';
import { zeitpunktVon } from '../daten/kalender';
import { Speicherfehler } from '../daten/speichern';
import { alsKlarfehler, type Klarfehler } from '../daten/fehler';
import {
  offenePunkteLaden,
  PUNKTART,
  type OffenerPunkt,
  type Punktart,
} from '../daten/offen';
import { konfliktErledigen } from '../daten/belege';
import {
  bestaetigen,
  kundenLaden,
  kundeZusammenfuehren,
  orteLaden,
  ortZusammenfuehren,
  umbenennen,
  type Kunde,
  type Ort,
} from '../daten/stammdaten';
import { ZusammenfuehrenFormular } from './Zusammenfuehren';

function seitText(wert: string): string {
  const tage = Math.floor((Date.now() - zeitpunktVon(wert)) / 86_400_000);
  if (tage <= 0) return 'heute';
  if (tage === 1) return 'seit gestern';
  return `seit ${tage} Tagen`;
}

export function OffenePunkteSeite() {
  const liste = useAbfrage(offenePunkteLaden, []);
  const orte = useAbfrage(orteLaden, []);
  const kunden = useAbfrage(kundenLaden, []);
  const gehZu = useNavigate();

  const [fehler, setFehler] = useState<Klarfehler | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);
  const [offenerKonflikt, setOffenerKonflikt] = useState<OffenerPunkt | null>(null);
  const [zusammen, setZusammen] = useState<OffenerPunkt | null>(null);
  const [umbenennenFuer, setUmbenennenFuer] = useState<OffenerPunkt | null>(null);

  /**
   * Aufgefrischt wird auch nach einem Fehler (H1.6). Scheitert etwas an der
   * Änderungsprüfung, hat jemand anderes den Eintrag inzwischen angefasst —
   * und der neue Stand gehört auf den Bildschirm, sonst scheitert der nächste
   * Versuch am selben alten Stand, und man sieht nicht, wogegen.
   */
  async function mitFehlern(schluessel: string, tun: () => Promise<void>) {
    setFehler(null);
    setBeschaeftigt(schluessel);
    try {
      await tun();
    } catch (problem) {
      setFehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
    } finally {
      liste.auffrischen();
      orte.auffrischen();
      kunden.auffrischen();
      setBeschaeftigt(null);
    }
  }

  const punkte = liste.daten ?? [];

  /**
   * Die offenen Formulare arbeiten mit dem Punkt, wie er JETZT in der Liste
   * steht, nicht wie beim Öffnen. Nach einem Konflikt frischt die Liste auf —
   * das Formular behält die Eingabe (gleicher `key`), aber Name und Stand
   * kommen vom frischen Punkt. Ist der Punkt weg (jemand hat ihn erledigt
   * oder bestätigt), bleibt das Formular mit der Eingabe stehen und sagt das,
   * statt sich still zu schließen.
   */
  const jetzt = (punkt: OffenerPunkt | null) =>
    punkt ? punkte.find((p) => p.schluessel === punkt.schluessel) : undefined;
  const konfliktJetzt = jetzt(offenerKonflikt);
  const umbenennenJetzt = jetzt(umbenennenFuer);

  const zaehler = useMemo(() => {
    const gesammelt: Record<Punktart, number> = {
      konflikt: 0,
      belege: 0,
      fahrt: 0,
      stammdaten: 0,
      zeit: 0,
    };
    for (const punkt of punkte) gesammelt[punkt.art] += 1;
    return gesammelt;
  }, [punkte]);

  const auswahlZumZusammenfuehren: (Ort | Kunde)[] = useMemo(() => {
    if (!zusammen?.stammsatz) return [];
    return zusammen.stammsatz.art === 'ort' ? (orte.daten ?? []) : (kunden.daten ?? []);
  }, [zusammen, orte.daten, kunden.daten]);

  const spalten: Spalte<OffenerPunkt>[] = [
    {
      schluessel: 'art',
      text: 'Art',
      auswahl: true,
      wert: (p) => PUNKTART[p.art].text,
      zeige: (p) => (
        <Marker art={PUNKTART[p.art].farbe}>{PUNKTART[p.art].text}</Marker>
      ),
    },
    { schluessel: 'was', text: 'Was ist offen', wert: (p) => p.was },
    {
      schluessel: 'wobei',
      text: 'Wobei',
      auswahl: true,
      wert: (p) => p.wobei,
      zeige: (p) => <strong>{p.wobei}</strong>,
    },
    {
      schluessel: 'hinweis',
      text: 'Einzelheiten',
      wert: (p) => p.hinweis ?? '',
      zeige: (p) => <span className="meta">{p.hinweis ?? '—'}</span>,
    },
    {
      schluessel: 'seit',
      text: 'Liegt',
      wert: (p) => p.seit,
      // In dieser Spalte stehen Tage UND Zeitpunkte. Sortiert wird nach
      // derselben Rechnung wie die Anzeige „seit 3 Tagen" (H1.5) — und nach
      // dem ALTER, nicht nach dem Zeitpunkt: Aufsteigend heißt hier „seit
      // heute, seit gestern, seit 5 Tagen", so wie die Zahlen in der Zelle
      // steigen. Nach dem Zeitpunkt sortiert, wäre der erste Klick (↑) genau
      // die Grundreihenfolge — ein Klick ohne sichtbare Wirkung.
      sortierwert: (p) => -zeitpunktVon(p.seit),
      zeige: (p) => <span className="meta">{seitText(p.seit)}</span>,
    },
    {
      schluessel: 'tun',
      text: '',
      rechts: true,
      nichtKuerzen: true,
      wert: () => '',
      zeige: (p) => (
        <div className="reihe reihe--rechts" onClick={(e) => e.stopPropagation()}>
          {p.art === 'belege' && p.ziel && (
            <Knopf art="zweit" klein onClick={() => gehZu(p.ziel!)}>
              Zur Tour
            </Knopf>
          )}
          {p.art === 'konflikt' && (
            <Knopf art="zweit" klein onClick={() => setOffenerKonflikt(p)}>
              Erledigen
            </Knopf>
          )}
          {p.art === 'stammdaten' && (
            <>
              <Knopf art="still" klein onClick={() => setUmbenennenFuer(p)}>
                Umbenennen
              </Knopf>
              <Knopf art="still" klein onClick={() => setZusammen(p)}>
                Zusammenführen
              </Knopf>
              <Knopf
                art="zweit"
                klein
                disabled={beschaeftigt === p.schluessel}
                onClick={() =>
                  void mitFehlern(p.schluessel, () => bestaetigen(p.stammsatz!))
                }
              >
                Bestätigen
              </Knopf>
            </>
          )}
          {p.art === 'zeit' && (
            <Knopf art="still" klein onClick={() => gehZu('/zeiten')}>
              Zu den Zeiten
            </Knopf>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Seitenkopf
        titel="Offene Punkte"
        erklaerung="Alles, was vor der Abrechnung noch anzufassen ist — fehlende Belege, unterwegs angelegte Orte und Kunden, Zuordnungskonflikte und Arbeitstage ohne Zeit. Das Älteste steht oben."
        werkzeuge={
          <Knopf art="still" onClick={liste.auffrischen}>
            Neu laden
          </Knopf>
        }
      />

      {fehler && (
        <div style={{ marginBottom: 16 }}>
          <Fehlerkasten fehler={fehler} />
        </div>
      )}

      {punkte.length > 0 && (
        <div className="vorausschau">
          {(Object.keys(PUNKTART) as Punktart[])
            .filter((art) => zaehler[art] > 0)
            .map((art) => (
              <div className="vorausschau__block" key={art}>
                <span className="vorausschau__marke">{PUNKTART[art].text}</span>
                <strong className="vorausschau__summe">{zaehler[art]}</strong>
                <span className="meta">
                  {zaehler[art] === 1 ? 'offener Punkt' : 'offene Punkte'}
                </span>
              </div>
            ))}
        </div>
      )}

      {offenerKonflikt && (
        <div style={{ marginBottom: 16 }}>
          <KonfliktFormular
            key={offenerKonflikt.schluessel}
            punkt={konfliktJetzt ?? offenerKonflikt}
            verschwunden={!konfliktJetzt && !liste.laedt}
            laeuft={beschaeftigt === offenerKonflikt.schluessel}
            abbrechen={() => setOffenerKonflikt(null)}
            bestaetigen={(notiz) =>
              void mitFehlern(offenerKonflikt.schluessel, async () => {
                await konfliktErledigen(offenerKonflikt.konfliktId!, notiz);
                setOffenerKonflikt(null);
              })
            }
          />
        </div>
      )}

      {zusammen?.stammsatz && (
        <div style={{ marginBottom: 16 }}>
          {/* Siehe oben: ohne Schlüssel überdauert das gewählte Ziel den
              Wechsel der Quelle. */}
          <ZusammenfuehrenFormular
            key={zusammen.schluessel}
            quelle={zusammen.stammsatz}
            auswahl={auswahlZumZusammenfuehren}
            wortFuerEinen={zusammen.stammsatz.art === 'ort' ? 'Ort' : 'Kunden'}
            laeuft={beschaeftigt === zusammen.schluessel}
            abbrechen={() => setZusammen(null)}
            bestaetigen={(zielId) =>
              void mitFehlern(zusammen.schluessel, async () => {
                if (zusammen.stammsatz!.art === 'ort') {
                  await ortZusammenfuehren(zusammen.stammsatz!.id, zielId);
                } else {
                  await kundeZusammenfuehren(zusammen.stammsatz!.id, zielId);
                }
                setZusammen(null);
              })
            }
          />
        </div>
      )}

      {umbenennenFuer?.stammsatz && (
        <div style={{ marginBottom: 16 }}>
          {/* Ohne Schlüssel behält das Formular beim Wechsel des Eintrags
              seinen alten Inhalt — und benennt dann den zweiten Eintrag auf den
              Namen des ersten um. */}
          <UmbenennenFormular
            key={umbenennenFuer.schluessel}
            name={(umbenennenJetzt ?? umbenennenFuer).stammsatz!.name}
            nameBeimOeffnen={umbenennenFuer.stammsatz.name}
            verschwunden={!umbenennenJetzt && !liste.laedt}
            laeuft={beschaeftigt === umbenennenFuer.schluessel}
            abbrechen={() => setUmbenennenFuer(null)}
            bestaetigen={(neuerName) =>
              void mitFehlern(umbenennenFuer.schluessel, async () => {
                await umbenennen(
                  (umbenennenJetzt ?? umbenennenFuer).stammsatz!,
                  neuerName,
                );
                setUmbenennenFuer(null);
              })
            }
          />
        </div>
      )}

      <Karte titel="Zu erledigen">
        {liste.laedt && !liste.daten ? (
          <Ladehinweis />
        ) : liste.fehler ? (
          <Fehlerkasten fehler={liste.fehler} nochmal={liste.auffrischen} />
        ) : punkte.length === 0 ? (
          <Meldung art="gut" titel="Nichts offen">
            Keine fehlenden Belege, keine Tour ohne Fahrt, keine ungeprüften
            Stammdaten, keine Zuordnungskonflikte und keine Arbeitstage ohne
            Zeit. Das kommt selten vor — jetzt wäre ein guter Moment zum
            Abrechnen.
          </Meldung>
        ) : (
          <Filtertabelle
            spalten={spalten}
            zeilen={punkte}
            schluessel={(p) => p.schluessel}
            leer="Nichts offen."
            zaehlung={(gezeigt, gesamt) =>
              gezeigt === gesamt
                ? `${gesamt} offene Punkte`
                : `${gezeigt} von ${gesamt} offenen Punkten`
            }
          />
        )}
      </Karte>
    </>
  );
}

/**
 * Ein Zuordnungskonflikt wird nicht einfach weggeklickt.
 *
 * „Erledigt" heißt: Jemand hat hingeschaut und entschieden — nicht: Es hat
 * sich von selbst gelöst. In einem halben Jahr ist die Frage nie „gab es einen
 * Konflikt", sondern „was hat man damals daraus gemacht". Deshalb die Notiz.
 */
function KonfliktFormular(eigenschaften: {
  punkt: OffenerPunkt;
  /** Der Konflikt steht nicht mehr in der Liste — erledigt oder weg. */
  verschwunden: boolean;
  laeuft: boolean;
  abbrechen: () => void;
  bestaetigen: (notiz: string) => void;
}) {
  const [notiz, setNotiz] = useState('');

  return (
    <Karte titel="Zuordnungskonflikt erledigen">
      <div className="stapel" style={{ maxWidth: 640 }}>
        <p className="meta">{eigenschaften.punkt.hinweis}</p>
        {eigenschaften.verschwunden && (
          <Meldung art="warnung" titel="Steht nicht mehr in den offenen Punkten">
            Jemand hat diesen Konflikt inzwischen erledigt, oder er ist weg.
            Deine Notiz steht noch hier, damit nichts verloren geht —
            gespeichert wird sie nicht mehr.
          </Meldung>
        )}
        <Textfeld
          marke="Was wurde daraus?"
          wert={notiz}
          setzen={setNotiz}
          autoFocus
          platzhalter="z. B. Fuhren auf 26-0014 umgebucht, Fahrer informiert"
          hinweis="Wird mitgeschrieben. Ohne Notiz weiß in einem halben Jahr niemand mehr, was hier entschieden wurde."
        />
        <div className="reihe reihe--rechts">
          <Knopf art="still" onClick={eigenschaften.abbrechen}>
            Abbrechen
          </Knopf>
          <Knopf
            art="haupt"
            disabled={
              notiz.trim().length < 3 || eigenschaften.laeuft || eigenschaften.verschwunden
            }
            onClick={() => eigenschaften.bestaetigen(notiz)}
          >
            {eigenschaften.laeuft ? 'Wird gespeichert …' : 'Erledigt'}
          </Knopf>
        </div>
      </div>
    </Karte>
  );
}

/**
 * Umbenennen bestätigt gleich mit: Wer den Namen geraderückt, hat hingeschaut.
 */
function UmbenennenFormular(eigenschaften: {
  /** Der Name, wie er JETZT in der Liste steht. */
  name: string;
  /** Der Name beim Öffnen — weicht er ab, hat jemand dazwischen umbenannt. */
  nameBeimOeffnen: string;
  verschwunden: boolean;
  laeuft: boolean;
  abbrechen: () => void;
  bestaetigen: (name: string) => void;
}) {
  const [name, setName] = useState(eigenschaften.name);
  const geaendert = name.trim() !== '' && name.trim() !== eigenschaften.name;
  const fremdUmbenannt = eigenschaften.name !== eigenschaften.nameBeimOeffnen;

  return (
    <Karte titel={`„${eigenschaften.name}“ umbenennen`}>
      <div className="stapel" style={{ maxWidth: 480 }}>
        {eigenschaften.verschwunden ? (
          <Meldung art="warnung" titel="Steht nicht mehr in den offenen Punkten">
            Jemand hat diesen Eintrag inzwischen bestätigt oder zusammengeführt.
            Umbenennen lässt er sich jetzt unter Stammdaten.
          </Meldung>
        ) : (
          fremdUmbenannt && (
            <Meldung art="warnung" titel="Inzwischen umbenannt">
              Beim Öffnen hieß der Eintrag „{eigenschaften.nameBeimOeffnen}“,
              jetzt heißt er „{eigenschaften.name}“. Speichern ersetzt diesen
              Namen durch deine Eingabe.
            </Meldung>
          )
        )}
        <Textfeld
          marke="Name"
          wert={name}
          setzen={setName}
          autoFocus
          hinweis="Der Eintrag gilt danach als geprüft. Alles, was daran hängt, bleibt daran hängen."
        />
        <div className="reihe reihe--rechts">
          <Knopf art="still" onClick={eigenschaften.abbrechen}>
            Abbrechen
          </Knopf>
          <Knopf
            art="haupt"
            disabled={!geaendert || eigenschaften.laeuft || eigenschaften.verschwunden}
            onClick={() => eigenschaften.bestaetigen(name)}
          >
            {eigenschaften.laeuft ? 'Wird gespeichert …' : 'Umbenennen und bestätigen'}
          </Knopf>
        </div>
      </div>
    </Karte>
  );
}
